interface DebounceOptions<T extends any[]> {
  delay?: number
  immediate?: (...args: T) => boolean
}

export function useDebounce<T extends any[]>(
  fn: (...args: T) => void,
  options?: DebounceOptions<T>,
): (...args: T) => void {
  let handle: NodeJS.Timeout

  return function handler(...args: T): void {
    clearTimeout(handle)

    if (options?.immediate?.(...args)) {
      fn(...args)
    } else {
      handle = setTimeout(
        () => fn(...args),
        options?.delay ?? 100,
      )
    }
  }
}

export function useDebounceWithResponse<T extends any[], R extends any>(
  fn: (...args: T) => R | Promise<R>,
  options?: DebounceOptions<T>,
): (...args: T) => Promise<R> {
  let handle: NodeJS.Timeout
  let resolves: ((value: R | PromiseLike<R>) => void)[] = []

  return function handler(...args: T): Promise<R> {
    clearTimeout(handle)

    if (options?.immediate?.(...args)) {
      return Promise
        .resolve(fn(...args))
    }

    return new Promise(
      resolve => {
        resolves.push(resolve)

        handle = setTimeout(
          () => {
            let temp: R

            Promise
              .resolve(fn(...args))
              .then(
                res => {
                  temp = res
                },
              )
              .finally(
                () => {
                  resolves
                    .forEach(r => r(temp))

                  resolves = []
                },
              )
          },
          options?.delay ?? 100,
        )
      },
    )
  }
}
